/**
 * THE ATTENTION BAND — the ranked "needs a human" list at the top of the
 * Command Center.
 *
 * WHY THIS IS A PURE MODULE. The web test runner is configured with
 * `environment: "node"` (vitest.config.ts) and cannot render components, so the
 * only way this ranking can be tested at all is to keep it out of the JSX. It
 * is also the one piece of the dashboard whose *order* is load-bearing: the row
 * an operator reads first is the row they act on, so the ordering is pinned by
 * tests rather than left to the sequence the Body happens to write.
 *
 * THE CONTRACT, in three parts:
 *
 *  1. A signal is emitted ONLY when its count is a finite number greater than
 *     zero. A zero-count row is not "good news" — it is noise that pushes the
 *     real finding down the list. This is why the band cannot be built by
 *     rendering a fixed set of rows and dimming the empty ones.
 *     The one exception carries NO count: a check that could not run
 *     (`tickets-unchecked`). It exists precisely so that a failed read cannot
 *     fall through to the all-clear line — "nothing found" and "could not
 *     look" are different facts, and only one of them is good news.
 *
 *  2. Ranking is: pinned first, then severity (error → warning → info), then
 *     the larger count, then declaration order. "Pinned" exists for exactly one
 *     source — critical capabilities that are not ready — because a single
 *     capability that CANNOT SUCCEED outranks any volume of routine work. Two
 *     unhealthy instances are a Tuesday; "accepted action items cannot become
 *     work items" is why 31 of them silently piled up (see the API's
 *     capability-readiness.types.ts for that incident).
 *
 *  3. Every signal carries the route that FIXES it. A count with no destination
 *     is a complaint, not a dashboard.
 *
 * DELIBERATELY ABSENT: new integration errors. `integration-errors` is an
 * admin-tier service with no registered web ServiceStore, and adding one to
 * surface a number here would widen an admin surface for a badge. Operator
 * ruling: skip it.
 */
import type { DashboardSummary } from "../home.types";

/** Maps 1:1 onto the MUI palette roles the row's dot and text use. */
export type AttentionSeverity = "error" | "warning" | "info";

export interface AttentionSignal {
  /** Stable key — also the React key, so rows never re-mount across polls. */
  id: string;
  severity: AttentionSeverity;
  /** null only for a check that could not run — there is nothing to count. */
  count: number | null;
  /** The phrase WITHOUT the count, already pluralised for `count`. */
  label: string;
  /** `${count} ${label}` — the full sentence, for a11y labels and tests; the label alone when there is no count. */
  message: string;
  /** RouterStore route name that resolves this signal. */
  route: string;
  /** Outranks severity. True only for critical-capability findings. */
  pinned: boolean;
}

export interface AttentionInputs {
  /** The dashboard rollup; null while the first poll is in flight. */
  summary: DashboardSummary | null | undefined;
  /**
   * `capability-readiness.summary.criticalUnready` — admin-only. Non-admins
   * never load readiness (the service 403s), and pass 0/undefined, which simply
   * omits the row. The dashboard must not show a hole where the admin band is.
   */
  criticalUnready?: number | null;
  /**
   * Tickets waiting on the signed-in person (assigned to them, or routed to
   * their team and not yet taken). null/undefined while unknown — omitted.
   */
  ticketsWaitingOnMe?: number | null;
  /**
   * The tickets count read FAILED (swallowed by the ServiceStore, or thrown).
   * Emits the `tickets-unchecked` warning IN PLACE OF the count row: a number
   * that could not be re-verified is not shown as current, and an empty band
   * is not shown as clear.
   */
  ticketsCheckFailed?: boolean;
}

/**
 * The row for sections the API reported unavailable. Its route is the
 * dashboard itself — the fix is a re-read (the Body re-runs the load for it)
 * or the server log, which is where the failure's reason lives.
 */
export const DASHBOARD_UNAVAILABLE_SIGNAL_ID = "dashboard-sections-unavailable";

const SEVERITY_RANK: Record<AttentionSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

/** A count is a signal only when it is a real, positive number. */
const isActionable = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n) && n > 0;

interface RankedSignal extends AttentionSignal {
  /** Declaration index — the final, deterministic tiebreak. */
  order: number;
}

/**
 * Build the ranked attention list.
 *
 * Returns `[]` when nothing is outstanding — which the Body renders as the
 * all-clear line, NOT as an empty box. Callers must separately check whether
 * the first load has finished: an empty list from a null summary means "not
 * known yet", and rendering "all clear" from it would be a confident lie.
 */
export function deriveAttentionSignals(
  inputs: AttentionInputs
): AttentionSignal[] {
  const s = inputs.summary;
  const ranked: RankedSignal[] = [];

  const push = (
    id: string,
    severity: AttentionSeverity,
    count: unknown,
    one: string,
    many: string,
    route: string,
    pinned = false
  ) => {
    if (!isActionable(count)) return;
    const label = count === 1 ? one : many;
    ranked.push({
      id,
      severity,
      count,
      label,
      message: `${count} ${label}`,
      route,
      pinned,
      order: ranked.length,
    });
  };

  // Declaration order below IS the tiebreak order — worst-consequence first.
  push(
    "critical-capabilities",
    "error",
    inputs.criticalUnready,
    "critical capability is not ready",
    "critical capabilities are not ready",
    "SystemHealth",
    true
  );
  if (inputs.ticketsCheckFailed) {
    // A CHECK THAT COULD NOT RUN is not "nothing found". The count is a
    // separate read from the rollup; when it fails, the band used to settle
    // with no row and render "All clear" while an unknown number of tickets
    // waited on this person. No count, by design — the row names what is
    // unknown and routes to where it can be seen.
    ranked.push({
      id: "tickets-unchecked",
      severity: "warning",
      count: null,
      label: "Tickets could not be checked",
      message: "Tickets could not be checked",
      route: "Tickets",
      pinned: false,
      order: ranked.length,
    });
  } else {
    push(
      "tickets-waiting",
      "warning",
      inputs.ticketsWaitingOnMe,
      "ticket is waiting on you",
      "tickets are waiting on you",
      "Tickets"
    );
  }
  // Sections of the rollup whose query FAILED. Their blocks hold zeros that
  // are not counts, so every row below that reads one is silently absent —
  // and an empty band would then print "All clear" over data nobody could
  // read. This row stands in for all of them: it keeps the verdict honest and
  // says how much of the page is not reporting.
  push(
    DASHBOARD_UNAVAILABLE_SIGNAL_ID,
    "warning",
    s?.unavailable?.length,
    "dashboard section could not be loaded",
    "dashboard sections could not be loaded",
    "Home"
  );
  // `pendingReview` on the wire is "distributed and NOBODY HAS ACCEPTED IT"
  // since 2026-09-18 — the field name is a wire format the API kept, not a
  // claim that anything is waiting for a review. Nothing reviews items now.
  push(
    "pending-review",
    "warning",
    s?.assignments?.pendingReview,
    "action item nobody has accepted",
    "action items nobody has accepted",
    "ReviewQueue"
  );
  push(
    "awaiting-mapping",
    "warning",
    s?.meetings?.awaitingMapping,
    "meeting awaiting client mapping",
    "meetings awaiting client mapping",
    "MappingQueue"
  );
  push(
    "overdue",
    "warning",
    s?.assignments?.overdue,
    "action item is overdue",
    "action items are overdue",
    "ReviewQueue"
  );
  push(
    "unhealthy-instances",
    "error",
    s?.instances?.unhealthy,
    "client instance is unhealthy",
    "client instances are unhealthy",
    "Client Instances"
  );
  push(
    "out-of-sync",
    "warning",
    s?.instances?.outOfSync,
    "instance is running a different image than expected",
    "instances are running a different image than expected",
    "DeploymentStatus"
  );
  push(
    "updates-available",
    "info",
    s?.instances?.updatesAvailable,
    "instance has a newer image available",
    "instances have a newer image available",
    "DeploymentStatus"
  );

  ranked.sort(
    (a, b) =>
      Number(b.pinned) - Number(a.pinned) ||
      SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
      // A countless row sorts as 0: under every counted row of its severity.
      (b.count ?? 0) - (a.count ?? 0) ||
      a.order - b.order
  );

  return ranked.map((signal) => ({
    id: signal.id,
    severity: signal.severity,
    count: signal.count,
    label: signal.label,
    message: signal.message,
    route: signal.route,
    pinned: signal.pinned,
  }));
}
